import { useRef } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { motion, useScroll, useTransform } from "framer-motion";
import { Radio, ArrowRight, Instagram, Check } from "lucide-react";
import { Reveal, MaskLines } from "@/components/Reveal";
import { ASSETS, TEXTURES, MESSAGES_LINKS } from "@/lib/assets";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* ---------------- HERO ---------------- */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden vignette">
        <motion.div
          style={{ y: bgY }}
          className="absolute inset-0 -z-0"
          aria-hidden
        >
          <img src={TEXTURES.concrete} alt="" className="h-[130%] w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-charcoal/60" />
        </motion.div>

        <motion.div
          style={{ y: contentY, opacity: fade }}
          className="relative z-10 h-full flex flex-col justify-center max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 1 }}
            className="font-label uppercase tracking-[0.4em] text-crimson-bright text-xs md:text-sm mb-6"
          >
            Independent Metal - Rock - Alternative
          </motion.p>

          <h1 className="font-display font-black text-softwhite leading-[0.95] text-5xl sm:text-6xl lg:text-8xl tracking-tight">
            <MaskLines lines={["SORROW &", "SERENITY MUSIC"]} delay={0.3} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="mt-8 max-w-xl text-lg md:text-xl text-softwhite/90 font-body"
          >
            Balancing Crushing Weight with Atmospheric Serenity.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 1 }}
            className="mt-3 max-w-xl text-slate font-body"
          >
            An independent home for all things rock and metal - the voice for the voiceless.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 1 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/radio"
              data-testid="hero-listen-btn"
              className="group inline-flex items-center justify-center gap-3 bg-crimson hover:bg-crimson-bright text-softwhite px-8 py-4 font-label uppercase tracking-[0.15em] text-sm transition-colors duration-300"
            >
              <Radio size={18} /> Listen to KSSR Radio
            </Link>
            <Link
              to="/roster"
              data-testid="hero-roster-btn"
              className="group inline-flex items-center justify-center gap-3 border border-slate/50 hover:border-softwhite text-softwhite px-8 py-4 font-label uppercase tracking-[0.15em] text-sm transition-colors duration-300"
            >
              Explore Roster
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </motion.div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-slate font-label uppercase tracking-[0.3em] text-[10px] animate-pulse-glow">
          Scroll
        </div>
      </section>

      {/* ---------------- MARQUEE ---------------- */}
      <section className="relative z-10 border-y border-slate/15 bg-charcoal py-6 overflow-hidden">
        <Marquee speed={40} gradient={false} autoFill>
          {["MODERN METAL", "GOTHIC ALTERNATIVE", "ATMOSPHERIC ROCK", "POST-ROCK", "DEATHCORE", "SHOEGAZE"].map(
            (w, i) => (
              <span key={i} className="mx-8 font-display text-3xl md:text-5xl text-outline uppercase">
                {w} <span className="text-crimson mx-6">✳</span>
              </span>
            )
          )}
        </Marquee>
      </section>

      {/* ---------------- MANIFESTO / THE ETHOS ---------------- */}
      <section className="relative z-10 bg-charcoal py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16">
          <Reveal>
            <p className="font-label uppercase tracking-[0.35em] text-crimson-bright text-xs mb-10">The Ethos</p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="border-l-2 border-crimson pl-6 md:pl-12 max-w-5xl">
              <p
                data-testid="ethos-statement"
                className="font-display text-softwhite leading-[1.25] text-2xl sm:text-3xl md:text-4xl"
              >
                Sorrow and Serenity Music is an independent home championing all things rock and metal -
                <span className="text-crimson-bright"> no compromise, no polish for the sake of mainstream - come as you are.</span>{" "}
                Direct-licensed airplay, honest support, and a roster curated on craft alone.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- FEATURED ARTIST ---------------- */}
      <section className="relative z-10 bg-charcoal pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16">
          <Reveal>
            <p className="font-label uppercase tracking-[0.35em] text-crimson-bright text-xs mb-10">Flagship Roster</p>
          </Reveal>

          <div className="grid lg:grid-cols-2 border border-slate/20">
            {/* Left: logo on textured canvas */}
            <div className="relative min-h-[340px] lg:min-h-[520px] flex items-center justify-center overflow-hidden border-b lg:border-b-0 lg:border-r border-slate/20">
              <img src={TEXTURES.gothic} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
              <div className="absolute inset-0 bg-charcoal/50" />
              <Reveal className="relative z-10 px-8">
                <img src={ASSETS.messagesLogo} alt="MESSAGES" className="w-full max-w-sm object-contain" />
              </Reveal>
            </div>

            {/* Right: details */}
            <div className="bg-surface p-8 md:p-14 flex flex-col justify-center">
              <Reveal>
                <span className="inline-block px-3 py-1.5 bg-crimson/20 border border-crimson/50 font-label uppercase tracking-[0.2em] text-[11px] text-crimson-bright">
                  New Single — Fall 2026
                </span>
                <h3 className="mt-6 font-display text-4xl md:text-5xl text-softwhite">MESSAGES</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["Modern Metal", "Gothic Alternative", "Atmospheric"].map((g) => (
                    <span key={g} className="font-label uppercase tracking-[0.12em] text-[11px] text-slate border border-slate/30 px-3 py-1">
                      {g}
                    </span>
                  ))}
                </div>
                <p className="mt-6 text-slate leading-relaxed font-body max-w-md">
                  Blending crushing modern metal grooves with soaring gothic atmosphere and dark melodic hooks.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Link
                    to="/roster"
                    data-testid="featured-view-artist-btn"
                    className="group inline-flex items-center gap-3 bg-crimson hover:bg-crimson-bright text-softwhite px-7 py-3.5 font-label uppercase tracking-[0.15em] text-sm transition-colors duration-300"
                  >
                    View Artist Page
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <a
                    href={MESSAGES_LINKS.spotify}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="featured-spotify-btn"
                    className="group inline-flex items-center gap-2.5 border border-slate/50 hover:border-softwhite text-softwhite px-6 py-3.5 font-label uppercase tracking-[0.15em] text-sm transition-colors duration-300"
                  >
                    <i className="fa-brands fa-spotify text-[#1DB954] text-base" aria-hidden="true" />
                    Listen on Spotify
                  </a>
                  <a
                    href={MESSAGES_LINKS.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="featured-instagram"
                    aria-label="Instagram"
                    className="text-slate hover:text-crimson-bright transition-colors"
                  >
                    <Instagram size={20} />
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- KSSR RADIO BANNER ---------------- */}
      <section className="relative z-10 overflow-hidden py-24 md:py-32">
        <img src={TEXTURES.crowd} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-charcoal/80" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <p className="font-label uppercase tracking-[0.35em] text-crimson-bright text-xs mb-4">The Underground, Amplified</p>
              <div className="border border-slate/20 bg-charcoal overflow-hidden">
                <img src={ASSETS.kssrBanner} alt="KSSR — Sorrow & Serenity Radio" className="w-full object-contain" />
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="bg-surface/80 backdrop-blur-md crimson-glow p-8 md:p-10">
                <p className="text-softwhite/90 text-lg font-body leading-relaxed">
                  <span className="text-crimson-bright font-semibold">KSSR</span> — Sorrow &amp; Serenity Radio is a
                  24/7 cloud broadcast dedicated exclusively to unsigned heavy music.
                </p>
                <ul className="mt-8 space-y-4">
                  {[
                    "100% Direct Licensed Unsigned Artists",
                    "Atmospheric Rock to Technical Deathcore",
                    "Zero Corporate Gatekeeping",
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-3 text-softwhite/90 font-body">
                      <Check size={18} className="text-crimson-bright shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/radio"
                  data-testid="banner-tune-in-btn"
                  className="mt-10 group inline-flex items-center gap-3 bg-crimson hover:bg-crimson-bright text-softwhite px-8 py-4 font-label uppercase tracking-[0.15em] text-sm transition-colors duration-300"
                >
                  <Radio size={18} /> Tune In Live
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
